import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { authCookies } from '@/api/cookies';
import { siteApi } from '@/api/endpoints/site';
import { Icon } from '@/shared/IconCollecion';
import Telegram from '@/assets/icons/social/Telegram.svg?react';
import Skype from '@/assets/icons/social/Skype.svg?react';
import VK from '@/assets/icons/social/VK.svg?react';

// Footer наш (у фронтендера в MainLayout его нет). Контент из
// siteApi.bootstrap().contacts — список ссылок. Иконки на новом стеке:
// IconCollection (Mail/External_Link/Arrow_Up_LG) + отдельные SVG из
// icons/social/ для соцсетей (Telegram/Skype/VK).
// brandTitle — build-time через VITE_BRAND_TITLE (не legacy BrandContext).

const brandTitle = import.meta.env.VITE_BRAND_TITLE ?? '';

const renderContactIcon = (fa: string) => {
  switch (fa) {
    case 'telegram':
      return <Telegram className="size-4 shrink-0" />;
    case 'skype':
      return <Skype className="size-4 shrink-0" />;
    case 'vk':
      return <VK className="size-4 shrink-0" />;
    case 'envelope-open':
      return <Icon name="Mail" color="currentColor" className="size-4 shrink-0" />;
    case 'external-link':
      return <Icon name="External_Link" color="currentColor" className="size-4 shrink-0" />;
    default:
      // Нейтральный fallback на случай нового типа контакта от бека, для
      // которого иконку не приготовили (FA-зависимость убрана с Flatkit).
      return <Icon name="External_Link" color="currentColor" className="size-4 shrink-0" />;
  }
};

const Footer = () => {
  const year = new Date().getFullYear();

  // Bootstrap дёргается во многих местах кабинета — react-query сам дедуплицирует.
  // Длинный staleTime: contacts редко меняются.
  const { data } = useQuery({
    queryKey: ['site-bootstrap'],
    queryFn: () => siteApi.bootstrap(),
    enabled: authCookies.hasFullAuth(),
    staleTime: 1000 * 60 * 30,
  });

  const contacts = data?.contacts ?? [];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const { t } = useTranslation('common');

  return (
    <footer className="mt-auto px-2 py-4 text-sm font-normal leading-5 text-content-secondary sm:px-4 lg:px-0 lg:pt-6">
      {/*
        Кастомный breakpoint 790px вместо стандартного sm (640px).
        На меньшей ширине динамические контакт-блоки + копирайт с
        brandTitle разъезжаются, копирайт «уезжает влево» под flex-wrap.
        790px эмпирически подобран под текущий набор contacts из БД.
        Если бек подсадит больше длинных контактов — поднять порог.
      */}
      <div className="flex flex-col items-center gap-3 min-[790px]:flex-row min-[790px]:justify-between">
        <div className="flex flex-wrap items-center justify-center gap-3 min-[790px]:justify-start min-[790px]:gap-4">
          {contacts.map((c, i) => (
            <a
              key={`${c.fa}-${i}`}
              href={`${c.shema}${c.address}`}
              title={c.name}
              aria-label={c.name}
              className="flex items-center gap-1 text-sm font-normal leading-5 text-content-secondary hover:text-content-accent"
            >
              {renderContactIcon(c.fa)}
              <span className="hidden min-[790px]:inline">{c.name}</span>
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span>
            {year} © <strong className="font-bold">{brandTitle}</strong>
          </span>
          <button
            type="button"
            onClick={scrollToTop}
            className="text-content-secondary hover:text-content-accent"
            aria-label={t('toTop')}
          >
            <Icon
              name="Arrow_Up_LG"
              color="currentColor"
              className="size-4 shrink-0"
            />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
